/**
 * The retired `noise` effect and `diffusion.latent_blend`, moved to their current homes: `noise.seed` → `diffusion.seed`, `noise.wiggle`/`pan`/`tilt` → `motion.*`, `diffusion.latent_blend` → `motion.output_smoothing`. The relocation table only reaches pre-chop flat keys, so a preset written after the chop needs this step.
 *
 * A leaked `diffusion.advanced` bag also carries the four server knobs that graduated into the contract: `renoise` → `diffusion.detail` (inverted, detail = 100 − renoise), `temporal_history` → `motion.temporal_smoothing`, `input_smoothing` → `motion.input_smoothing`, `interpolation` ('on'/'off') → `motion.interpolation` (boolean). The rest of the bag is left for `stripAdvancedFromSettings` / `conform`, which drop it.
 *
 * Idempotent by key evidence. A destination already present wins: a preset holding both spellings was written by a client that knew the new one.
 */
const isPlainObject = v => !!v && typeof v === 'object' && !Array.isArray(v)

const NOISE = {
	seed: ['diffusion', 'seed'],
	wiggle: ['motion', 'wiggle'],
	pan: ['motion', 'pan'],
	tilt: ['motion', 'tilt'],
}

const INTERPOLATION = { on: true, off: false, true: true, false: false }

const finite = v => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)

const ADVANCED = {
	renoise: ['diffusion', 'detail', v => (finite(v) === undefined ? undefined : 100 - v)],
	temporal_history: ['motion', 'temporal_smoothing', finite],
	input_smoothing: ['motion', 'input_smoothing', finite],
	interpolation: ['motion', 'interpolation', v => INTERPOLATION[String(v)]],
}

export function noiseToMotion(settings) {
	if (!isPlainObject(settings)) return settings
	const noise = settings.noise
	const diffusion = isPlainObject(settings.diffusion) ? settings.diffusion : undefined
	const advanced = isPlainObject(diffusion?.advanced) ? diffusion.advanced : undefined
	const hasNoise = 'noise' in settings
	const hasBlend = !!diffusion && 'latent_blend' in diffusion
	const graduated = advanced ? Object.keys(ADVANCED).filter(k => k in advanced) : []
	if (!hasNoise && !hasBlend && !graduated.length) return settings

	const out = { ...settings }
	delete out.noise
	const bags = {}
	const bag = effect => {
		if (!bags[effect]) bags[effect] = isPlainObject(settings[effect]) ? { ...settings[effect] } : {}
		return bags[effect]
	}
	const put = (effect, key, value) => {
		if (value === undefined) return
		const b = bag(effect)
		if (!(key in b)) b[key] = value
	}

	if (isPlainObject(noise)) {
		for (const [from, [effect, key]] of Object.entries(NOISE)) {
			if (from in noise) put(effect, key, noise[from])
		}
	}

	if (diffusion) {
		const d = bag('diffusion')
		if (hasBlend) {
			put('motion', 'output_smoothing', diffusion.latent_blend)
			delete d.latent_blend
		}
		if (graduated.length) {
			const rest = { ...advanced }
			for (const from of graduated) {
				const [effect, key, convert] = ADVANCED[from]
				put(effect, key, convert(advanced[from]))
				delete rest[from]
			}
			if (Object.keys(rest).length) d.advanced = rest
			else delete d.advanced
		}
	}

	Object.assign(out, bags)
	return out
}

export default noiseToMotion
